import { Liquid, FilterImplOptions, LiquidError } from "liquidjs";
import logger from "./logging";

export interface LiquidValidationResult {
    isValid: boolean;
    error?: string;
    suggestion?: string;
}

export class LiquidValidator {
    private engine: Liquid | null = null;
    private readonly translator: ((key: string, options?: any) => string) | undefined;

    constructor(translator?: (key: string, options?: any) => string) {
        this.translator = translator;
    }

    /**
     * Get or create the LiquidJS engine with custom filters (matching backend setup)
     */
    private getEngine(): Liquid {
        if (this.engine === null) {
            this.engine = this.buildEngine();
        }
        return this.engine;
    }

    /**
     * Build and configure the LiquidJS engine with custom filters
     */
    private buildEngine(): Liquid {
        const engine = new Liquid();

        // Add custom filters to match backend implementation
        const ifPresentFilter: FilterImplOptions = (initial, firstArg, secondArg, defaultValue) => {
            if (initial) {
                if (secondArg != null) {
                    return firstArg + initial + secondArg;
                } else {
                    return initial + firstArg;
                }
            } else {
                // if there is no default value, return empty string
                return defaultValue ?? "";
            }
        };

        engine.registerFilter("ifPresent", ifPresentFilter);

        // For downward compatibility
        engine.registerFilter("ifNotEmpty", ifPresentFilter);

        // Deprecated (for downward compatibility)
        engine.registerFilter("addNullSafe", ifPresentFilter);

        return engine;
    }

    /**
     * Validates liquid template syntax using the actual LiquidJS parser
     */
    validate(template: string): LiquidValidationResult {
        if (!template || template.trim() === "") {
            return { isValid: true };
        }

        try {
            const engine = this.getEngine();

            // Parse the template - this will throw if syntax is invalid
            engine.parse(template);

            return { isValid: true };
        } catch (error: unknown) {
            return {
                isValid: false,
                error: this.formatError(error),
                suggestion: this.getSuggestionForError(error),
            };
        }
    }

    /**
     * Format error message from LiquidJS to be user-friendly
     */
    private formatError(error: unknown): string {
        if (!LiquidError.is(error)) {
            logger.warnObject(error, "unknown liquidError");
            return this.translator ? this.translator("liquidValidator.errors.unknownError") : "Unknown syntax error";
        }

        let message = error.message || "Syntax error";

        // Remove internal LiquidJS stack traces and make more user-friendly
        if (message.includes("TokenizationError")) {
            message = message.replace(/^.*TokenizationError:\s*/, "");
        } else if (message.includes("ParseError")) {
            message = message.replace(/^.*ParseError:\s*/, "");
        } else if (message.includes("RenderError")) {
            message = message.replace(/^.*RenderError:\s*/, "");
        }

        // Remove file path references since we're validating inline templates
        message = message.replace(/\s*at\s+.*$/m, "");

        // Try to match and translate specific error patterns with precise regex
        if (this.translator) {
            // Pattern: tag "EXAMPLE" not closed, line:X, col:Y
            // Handle complex tag content that may contain escaped quotes
            let match = message.match(/^tag "(.+?)" not closed, line:(\d+), col:(\d+)$/);
            if (match) {
                const [, tag, , col] = match;
                return this.translator("liquidValidator.errors.tagNotClosed", { tag, position: col });
            }

            // Pattern: tag "EXAMPLE" not found, line:X, col:Y
            match = message.match(/^tag "([^"]+)" not found, line:(\d+), col:(\d+)$/);
            if (match) {
                const [, tag, , col] = match;
                return this.translator("liquidValidator.errors.tagNotFound", { tag, position: col });
            }

            // Pattern: invalid value expression: "EXAMPLE", line:X, col:Y
            match = message.match(/^invalid value expression: "([^"]*)", line:(\d+), col:(\d+)$/);
            if (match) {
                const [, expression, , col] = match;
                return this.translator("liquidValidator.errors.invalidExpression", { expression, position: col });
            }

            // Pattern: output "EXAMPLE" not closed, line:X, col:Y
            // Handle escaped quotes within the output content
            match = message.match(/^output "(.+?)" not closed, line:(\d+), col:(\d+)$/);
            if (match) {
                const [, output, , col] = match;
                return this.translator("liquidValidator.errors.outputNotClosed", { output, position: col });
            }

            // Pattern: undefined variable: EXAMPLE, line:X, col:Y
            match = message.match(/^undefined variable: ([^,]+), line:(\d+), col:(\d+)$/);
            if (match) {
                const [, variable, , col] = match;
                return this.translator("liquidValidator.errors.undefinedVariable", { variable, position: col });
            }

            // Pattern: undefined filter: EXAMPLE, line:X, col:Y
            match = message.match(/^undefined filter: ([^,]+), line:(\d+), col:(\d+)$/);
            if (match) {
                const [, filter, , col] = match;
                return this.translator("liquidValidator.errors.undefinedFilter", { filter, position: col });
            }

            // Pattern: illegal tag syntax, tag name expected, line:X, col:Y
            match = message.match(/^illegal tag syntax, tag name expected, line:(\d+), col:(\d+)$/);
            if (match) {
                const [, , col] = match;
                return this.translator("liquidValidator.errors.illegalTagSyntax", { position: col });
            }

            // Pattern: expected ":" after filter name, line:X, col:Y
            match = message.match(/^expected ":" after filter name, line:(\d+), col:(\d+)$/);
            if (match) {
                const [, , col] = match;
                return this.translator("liquidValidator.errors.expectedColonAfterFilter", { position: col });
            }
        }

        logger.warnObject(error, "liquid: no translation for error message: " + message);
        return message;
    }

    /**
     * Provide helpful suggestions based on the error type
     */
    private getSuggestionForError(error: unknown): string {
        if (!LiquidError.is(error)) {
            logger.warnObject(error, "unknown liquidError");
            return this.translator
                ? this.translator("liquidValidator.suggestions.generic")
                : "Check your Liquid syntax according to Liquid documentation";
        }

        const message = error.message || "";

        if (message.includes("Unexpected character")) {
            return this.translator
                ? this.translator("liquidValidator.suggestions.unexpectedCharacter")
                : "Check for invalid characters. Liquid variables should be wrapped in {{ }}";
        }

        if (message.includes("Unexpected token")) {
            return this.translator
                ? this.translator("liquidValidator.suggestions.unexpectedToken")
                : "Check your liquid syntax. Use {{ variable }} for output, {% tag %} for logic";
        }

        if (message.includes("Expected")) {
            return this.translator
                ? this.translator("liquidValidator.suggestions.missingClosing")
                : "Missing closing tag or bracket. Make sure all braces are properly closed";
        }

        if (message.includes("Unclosed")) {
            return this.translator
                ? this.translator("liquidValidator.suggestions.unclosedTag")
                : "Unclosed liquid tag. Make sure all {{ }} and {% %} tags are properly closed";
        }

        if (message.includes("Unknown filter")) {
            return this.translator
                ? this.translator("liquidValidator.suggestions.unknownFilter")
                : "Unknown filter. Available custom filters: ifPresent, ifNotEmpty, addNullSafe (deprecated)";
        }

        if (message.includes("filter")) {
            return this.translator
                ? this.translator("liquidValidator.suggestions.filterSyntax")
                : 'Filter syntax error. Use: {{ variable | filter }} or {{ variable | filter: "parameter" }}';
        }

        return this.translator
            ? this.translator("liquidValidator.suggestions.generic")
            : "Check your Liquid syntax according to Liquid documentation";
    }

    /**
     * Test if a template can be rendered with sample data
     * This is useful for more comprehensive validation
     */
    async validateWithSampleData(
        template: string,
        sampleData: Record<string, any> = {},
    ): Promise<LiquidValidationResult> {
        // First check syntax
        const syntaxResult = this.validate(template);
        if (!syntaxResult.isValid) {
            return syntaxResult;
        }

        try {
            const engine = this.getEngine();

            // Try to render with sample data
            const parsed = engine.parse(template);
            await engine.render(parsed, sampleData);

            return { isValid: true };
        } catch (error: any) {
            return {
                isValid: false,
                error: this.formatError(error),
                suggestion: this.translator
                    ? this.translator("liquidValidator.suggestions.undefinedVariable")
                    : "This template has valid syntax but may reference undefined variables",
            };
        }
    }

    /**
     * Get available liquid variables as suggestions
     */
    static getSuggestions(liquidVariables: Record<string, any> | null): string[] {
        if (!liquidVariables) return [];

        const suggestions: string[] = [];

        const addSuggestions = (obj: any, prefix: string = "") => {
            for (const [key, value] of Object.entries(obj)) {
                const fullKey = prefix ? `${prefix}.${key}` : key;
                suggestions.push(fullKey);

                // If value is an object, add nested properties (limit depth to avoid infinite recursion)
                if (typeof value === "object" && value !== null && prefix.split(".").length < 3) {
                    addSuggestions(value, fullKey);
                }
            }
        };

        addSuggestions(liquidVariables);

        return suggestions.sort();
    }

    /**
     * Get available filters as suggestions
     */
    static getFilterSuggestions(): string[] {
        return [
            // Built-in Liquid filters
            "upcase",
            "downcase",
            "capitalize",
            "strip",
            "lstrip",
            "rstrip",
            "truncate",
            "truncatewords",
            "replace",
            "replace_first",
            "remove",
            "remove_first",
            "split",
            "join",
            "reverse",
            "sort",
            "size",
            "length",
            "first",
            "last",
            "uniq",
            "map",
            "where",
            "group_by",
            "date",
            "default",
            "escape",
            "escape_once",
            "url_encode",
            "url_decode",
            "abs",
            "ceil",
            "floor",
            "round",
            "plus",
            "minus",
            "times",
            "divided_by",
            "modulo",
            "append",
            "prepend",
            "slice",
            "at_least",
            "at_most",
            // Custom filters (matching backend)
            "ifPresent",
            "ifNotEmpty",
            "addNullSafe",
        ].sort();
    }
}
